import React from "react"
import { Box, Typography, useTheme } from "@mui/material"
import type { PaymentPresentation } from "@/domain/order/order-details-payment-presentation"
import { PaymentTiming } from "@/types/enums/PaymentTiming"
import { PaymentStatusIndicator } from "@/components/order/PaymentStatusIndicator"
import { PaymentTimingIndicator } from "@/components/order/PaymentTimingIndicator"
import type { PaymentInlineIndicatorSize } from "@/components/order/PaymentInlineIndicator"
import { ORDER_TYPO_MUTED_LABEL } from "@/utils/order-status"

export type PaymentSummaryInlineDensity = "default" | "compact"

export type PaymentSummaryInlineProps = {
  presentation: Pick<PaymentPresentation, "statusLabel" | "statusVariant">
  paymentTiming: PaymentTiming
  density?: PaymentSummaryInlineDensity
}

export function PaymentSummaryInline({
  presentation,
  paymentTiming,
  density = "default",
}: PaymentSummaryInlineProps) {
  const theme = useTheme()
  const isCompact = density === "compact"
  const indicatorSize: PaymentInlineIndicatorSize = isCompact ? "compact" : "default"

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: theme.spacing(1),
        flexWrap: "wrap",
        width: "100%",
      }}
    >
      <Typography
        component="span"
        variant={isCompact ? "caption" : "subtitle2"}
        sx={{
          flexShrink: 0,
          ...(isCompact
            ? { ...ORDER_TYPO_MUTED_LABEL, color: theme.palette.text.secondary }
            : { fontWeight: theme.typography.fontWeightBold, color: theme.palette.text.primary }),
        }}
      >
        Payment
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: { xs: "flex-start", sm: "flex-end" },
          gap: theme.spacing(isCompact ? 1 : 1.5),
          flex: { xs: "1 1 100%", sm: "0 1 auto" },
          minWidth: 0,
        }}
      >
        <PaymentStatusIndicator presentation={presentation} size={indicatorSize} />
        <Box
  sx={{
    width: 3,
    height: 3,
    borderRadius: "50%",
    bgcolor: theme.palette.text.disabled,
    opacity: 0.6,
    flexShrink: 0,
  }}
/>
        <PaymentTimingIndicator paymentTiming={paymentTiming} size={indicatorSize} />
      </Box>
    </Box>
  )
}

export default PaymentSummaryInline
